/* „Nowy pomiar", the drawer off the right edge. KANON `pomiary-klienta-2026-09-18`,
 * `02-szuflada-nowy-pomiar-dark.png`.
 *
 * The chrome is the app's own drawer vocabulary — the progression sheet's scrim and slide, at this
 * surface's width — rather than a second one written here. What differs is that this drawer covers
 * the PAGE rather than one panel, so it is `fixed` and its scrim is the page's.
 *
 * The scrim's `backdrop-filter` is the canon's own `blur(6px)`. It is not a glass SURFACE: it is a
 * neutral dim under a panel, which is exactly the distinction the owner drew on 13 September 2026,
 * and the blur is the one token `tokens/colors.css` carries for it. */

.scrim {
  position: fixed;
  z-index: 40;
  inset: var(--space-0);
  background: var(--scrim);
  backdrop-filter: var(--blur-scrim);
  animation: measure-scrim-in var(--dur-fade) var(--ease-default) both;
}

.drawer {
  position: fixed;
  z-index: 41;
  display: flex;
  inset-block: var(--space-0);
  inset-inline-end: var(--space-0);
  flex-direction: column;
  width: var(--w-measure-drawer);
  max-width: 100vw;
  background: var(--bg-card);
  border-inline-start: var(--border-hairline) solid var(--border-2);

  /* Horizontal and NEUTRAL: the panel arrives from the right, so it is lit from the side it came
   * from. Never a coloured cast — the ban is absolute. */
  box-shadow: var(--shadow-sheet);
  line-height: var(--lh-auto);
  animation: measure-drawer-in var(--dur-panel) var(--ease-default) both;
}

@keyframes measure-drawer-in {
  from {
    opacity: 0;
    transform: translateX(var(--space-7));
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes measure-scrim-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* These two ARE CSS, so the media block reaches them. Held rather than delayed: a zero duration on
 * a `both`-filled entrance would leave the panel on its `from` keyframe. */
@media (prefers-reduced-motion: reduce) {
  .drawer,
  .scrim {
    animation: none;
  }
}

.head {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-5) var(--space-6) var(--space-3);
}

.title {
  flex: 1;
  margin: var(--space-0);
  font-size: var(--fs-measure-drawer-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-tight);
  color: var(--text);
  letter-spacing: var(--ls-heading);
}

.close {
  display: grid;
  place-items: center;
  width: var(--space-7);
  height: var(--space-7);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  border-radius: var(--radius-sm);
}

.close:hover {
  color: var(--text);
  border-color: var(--border-3);
}

.close:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* A real `<form>` around the fields AND the footer, so Enter saves from any field and the primary
 * is the form's own submit. It owns no geometry of its own — it inherits the column the drawer laid
 * out, which is what stops a wrapper element from moving anything. */
.form {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* The body scrolls and the foot does not — the whole of "fit or scroll, never clip" on a 620px
 * window, where nine inputs are taller than the viewport. */
.scroll {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  min-height: 0;
  padding: var(--space-0) var(--space-6) var(--space-5);
  overflow: hidden auto;
}

.intro {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-regular);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-10px);
  align-items: start;
}

.groupTitle {
  grid-column: 1 / -1;
  margin: var(--space-2) var(--space-0) var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.label {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  justify-content: space-between;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

/* The canon marks the one required field by colouring its label rather than by hanging a star off
 * it. `--accent-text` is the accent moved per theme for words, which is what it exists for. */
.labelRequired {
  font-weight: var(--fw-semibold);
  color: var(--accent-text);
}

.unit {
  font-style: normal;
  color: var(--text-3);
}

.input {
  width: 100%;
  height: var(--h-detail-btn);
  padding: 0 var(--space-10px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text);
  background: var(--bg-inset);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  border-radius: var(--radius-sm);
  font-variant-numeric: tabular-nums;
}

.input:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.inputInvalid {
  border-color: var(--danger-text-strong);
}

.fieldError {
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
}

.error {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
}

.foot {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  justify-content: flex-end;
  padding: var(--space-3) var(--space-6) var(--space-5);
  border-block-start: var(--border-hairline) solid var(--border);
}

/* How many fields refused, beside the button that refused. Always in the tree and empty when there
 * is nothing to say, so the alert exists before the text arrives; it takes the free width so the
 * two buttons keep their own place whether it speaks or not. */
.footAlert {
  flex: 1;
  margin: var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--danger-text-strong);
}

/* GEOMETRY ONLY on both — the fills are `shared/ui/ghostButton` and `shared/ui/inkButton`. */
.cancel,
.save {
  height: var(--h-detail-btn);
  padding: 0 var(--space-13px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-sm);
}

/* The canon's `.btn.bare`: the ghost's ink with no outline at all, so „Anuluj" reads as the quiet
 * way out beside the filled primary rather than as a second button of equal weight. */
.cancel {
  background: transparent;
  border-color: transparent;
}

.cancel:hover {
  border-color: transparent;
}
